import React, { useEffect, useState } from 'react';

import axios from 'axios';

import IGScreen from '../../../cards/IGscreen';
import IGImageCard from '../../../cards/IGImageCard';
import { generatePath, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { ALL, INSTAGRAM } from '../../../../../config/constants';

interface Props {
  filterArray: string[];
  setLoadingData: React.Dispatch<React.SetStateAction<boolean>>;
}

const InstagramFeedPreview: React.FC<Props> = ({
  filterArray,
  setLoadingData
}) => {
  const [unPublishedData, setUnPublishedData] = useState([]);
  const [publishedData, setPublishedData] = useState([]);
  const [nextPublished, setNextPublished] = useState('');
  const [nextUnPublished, setNextUnPublished] = useState('');
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [loadingPastMedia, setLoadingPastMedia] = useState(false);
  const [combinedArray, setCombinedArray] = useState([]);
  const [filteredCombinedData, setFilteredCombinedData] = useState([]);

  const instagramPublicationDatetime = useSelector(
    (state: any) =>
      state.lomavisCreator.postData[INSTAGRAM]?.planned_publication_datetime
  );
  const instagramTempPublicationDatetime = useSelector(
    (state: any) =>
      state.lomavisCreator.postData[INSTAGRAM]?.tempPublicationData?.datetime
  );
  const lomavisCreator = useSelector((state: any) => state.lomavisCreator);
  const useSeparateMediaFiles = useSelector(
    (state: any) => state.lomavisCreator.useSeparateMediaFiles
  );
  const instagramMedia = useSelector(
    (state: any) => state.lomavisCreator.postData[INSTAGRAM]?.media
  );
  const generalMedia = useSelector(
    (state: any) => state.lomavisCreator.postData[ALL]?.media
  );

  const currentDate = new Date();
  const sixMonthsAgo = new Date(currentDate);
  sixMonthsAgo.setMonth(currentDate.getMonth() - 6);

  const { t } = useTranslate();
  const { locationslug } = useParams();

  const fetchUnPublishedData = async (next) => {
    if (loadingMedia) {
      return;
    }

    setLoadingData(true);
    setLoadingMedia(true);

    const configData = {
      apiUrl: generatePath('/api/v1/images/crops/')
    };

    const apiUrlIsDefined = configData.apiUrl;
    if (!apiUrlIsDefined) return;
    try {
      let response;

      if (next && nextUnPublished) {
        response = await axios.get(nextUnPublished);
      } else if (!next) {
        response = await axios.get(apiUrlIsDefined, {
          params: {
            crop_config__use_as_instagram_post: true,
            is_instagram_story: false,
            planned_publication_datetime__gte: new Date().toISOString(),
            limit: 10,
            published_resource_deleted_on_platform: 0,
            ordering: '-planned_publication_datetime',
            location__slug: locationslug
          }
        });
        // setMediaTotalCount(response.data?.count);
      }

      response?.data?.next
        ? setNextUnPublished(response.data?.next)
        : setNextUnPublished('');

      let newData = [];
      newData = response?.data?.results?.map((item) => ({
        media_is_video: item?.media_is_video,
        multipost_post_status: item?.multipost_post_status,
        planned_publication_datetime: new Date(
          item?.planned_publication_datetime
        ),
        preview_image_url: item?.preview_image_url,
        upload_to_platform_status: item?.upload_to_platform_status,
        upload_to_platform_timestamp: null,
        uuid: item?.uuid
      }));

      if (next) {
        response?.data?.results?.forEach((item) => {
          setUnPublishedData((prevState) => [
            ...prevState,
            {
              media_is_video: item?.media_is_video,
              multipost_post_status: item?.multipost_post_status,
              planned_publication_datetime: new Date(
                item?.planned_publication_datetime
              ),
              preview_image_url: item?.preview_image_url,
              upload_to_platform_status: item?.upload_to_platform_status,
              upload_to_platform_timestamp: null,
              uuid: item?.uuid
            }
          ]);
        });
      } else if (!next) {
        setUnPublishedData(newData);
      }
      setLoadingData(false);
      setLoadingMedia(false);

      setUnPublishedData(newData);
    } catch (error) {
      setLoadingData(false);
      setLoadingMedia(false);
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchUnPublishedData(false);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const configData = {
        apiUrl: generatePath('/api/v1/images/crops/')
      };
      const apiUrlIsDefined = configData.apiUrl;
      if (!apiUrlIsDefined) return;
      try {
        const response = await axios.get(apiUrlIsDefined, {
          params: {
            crop_config__use_as_instagram_post: true,
            is_instagram_story: false,
            limit: 20,
            ordering: '-upload_to_platform_timestamp',
            location__slug: locationslug,
            published_resource_deleted_on_platform: 0,
            upload_to_platform_status: 3,
            upload_to_platform_timestamp__lte: new Date().toISOString()
          }
        });

        let newData = [];
        newData = response.data?.results?.map((item) => ({
          media_is_video: item?.media_is_video,
          multipost_post_status: 5,
          planned_publication_datetime: new Date(
            item?.planned_publication_datetime
          ),
          preview_image_url: item?.preview_image_url,
          upload_to_platform_status: item?.upload_to_platform_status,
          upload_to_platform_timestamp: new Date(
            item?.upload_to_platform_timestamp
          ),
          uuid: item?.uuid
        }));
        setPublishedData(newData);
      } catch (error) {
        console.error('Error fetching published data:', error);
      }

      setLoadingPastMedia(false);
    };

    setLoadingPastMedia(true);
    fetchData();
  }, []);

  useEffect(() => {
    setCombinedArray([...unPublishedData, ...publishedData]);
  }, [publishedData, unPublishedData]);

  useEffect(() => {
    let plannedPosts: any[] = [];
    let publishedPosts: any[] = [];

    const selectedMedia = useSeparateMediaFiles ? instagramMedia : generalMedia;

    const firstMedia = selectedMedia?.[0];
    const scheduledDateTime =
      instagramPublicationDatetime ||
      instagramTempPublicationDatetime ||
      (!useSeparateMediaFiles
        ? lomavisCreator.postData[ALL]?.tempPublicationData?.datetime
        : null);

    const hasMedia = selectedMedia?.length > 0;
    const previewUrl = firstMedia?.preview_image || firstMedia?.thumbnail_image;

    if (scheduledDateTime && hasMedia && previewUrl) {
      const normalizedDate = new Date(scheduledDateTime);
      plannedPosts.push({
        media_is_video: firstMedia?.media_type === 'video',
        multipost_post_status: 4,
        planned_publication_datetime: normalizedDate,
        preview_image_url: previewUrl,
        upload_to_platform_timestamp: null,
        uuid: 'newItem'
      });
    }

    if (filterArray.includes('Draft')) {
      plannedPosts = plannedPosts.concat(
        combinedArray?.filter((item) => item.multipost_post_status === 2) || []
      );
    }

    if (filterArray.includes('Approval')) {
      plannedPosts = plannedPosts.concat(
        combinedArray?.filter((item) => item.multipost_post_status === 3) || []
      );
    }

    if (filterArray.includes('Planned')) {
      plannedPosts = plannedPosts.concat(
        combinedArray?.filter((item) => item.multipost_post_status === 4) || []
      );
    }

    if (filterArray.includes('Published')) {
      publishedPosts = combinedArray?.filter(
        (item) => item.multipost_post_status === 5
      );
    }

    // Date comparison function
    const compareDates = (a, b) => {
      const dateA = new Date(
        a.upload_to_platform_timestamp || a.planned_publication_datetime
      ).getTime();
      const dateB = new Date(
        b.upload_to_platform_timestamp || b.planned_publication_datetime
      ).getTime();
      return dateB - dateA; // Sorting in ascending order based on date
    };

    // Combine and filter unique posts by uuid, then sort by date
    const uniqueCombinedData = [...plannedPosts, ...publishedPosts]
      .reduce((accumulator, current) => {
        if (!accumulator.some((item) => item.uuid === current.uuid)) {
          accumulator.push(current);
        }
        return accumulator;
      }, [])
      .sort(compareDates);

    setFilteredCombinedData(uniqueCombinedData);
  }, [
    filterArray,
    combinedArray,
    instagramPublicationDatetime,
    instagramTempPublicationDatetime,
    instagramMedia,
    generalMedia,
    useSeparateMediaFiles
  ]);

  return (
    <div
      style={{ overflowY: 'scroll', rowGap: '30px' }}
      className="w-100 d-flex flex-column h-100"
    >
      <IGScreen>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: '100%',
            paddingLeft: '3px',
            paddingRight: '3px',
            height: filteredCombinedData?.length < 1 && '80%',
            overflow: 'hidden'
          }}
          className="flex-fill"
        >
          {/* <IGImageCard
            src={""}
            status={2}
            mediaTypeVideo={false}
            date={"2034-04-11"}
            key={"fefsfsfsf"}
          /> */}

          {loadingMedia && loadingPastMedia && (
            <div className="d-flex h-50 w-100 justify-content-center align-items-center text-muted">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t('Loading...')}</span>
              </Spinner>
            </div>
          )}

          {!loadingMedia &&
            !loadingPastMedia &&
            filteredCombinedData?.length < 1 && (
              <div className="d-flex h-50 w-100 justify-content-center align-items-center text-muted">
                {t('IG_preview_empty')}
              </div>
            )}

          {filteredCombinedData?.map((item, index) => (
            <IGImageCard
              src={item?.preview_image_url}
              status={item?.multipost_post_status}
              mediaTypeVideo={item?.media_is_video}
              date={
                item?.planned_publication_datetime
                  ? item?.planned_publication_datetime
                  : item?.upload_to_platform_timestamp
              }
              uuid={item?.uuid}
              key={item?.uuid + index}
            />
          ))}

          {/* {Array.from({ length: 30 }, (_, index) => (
            <ContentLoader
              speed={2}
              width={95}
              height={95}
              viewBox="0 0 95 95"
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
            >
              <rect x="4" y="5" rx="0" ry="0" width="95" height="95" />
            </ContentLoader>
          ))} */}
        </div>
      </IGScreen>
    </div>
  );
};

export default InstagramFeedPreview;
